// src/pages/RestaurantOwner/Inventory.jsx - GST & ITR COMPLIANT VERSION
import React, { useState, useEffect } from 'react';
import {
  Package,
  Lock,
  TrendingDown,
  AlertTriangle,
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  ShoppingCart,
  Users,
  BarChart3,
  FileText,
  DollarSign,
  X,
  Loader,
  Crown,
  Check,
  IndianRupee,
  TrendingUp,
  Calendar,
  Activity
} from 'lucide-react';
import { doc, setDoc, updateDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import StorageService from '../../services/StorageService';

const Inventory = ({ restaurantId }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [restaurantGSTIN, setRestaurantGSTIN] = useState('');
  const [restaurant, setRestaurant] = useState(null);

  const categories = [
    'Vegetables', 'Fruits', 'Meat & Poultry', 'Seafood', 'Dairy',
    'Grains & Cereals', 'Spices', 'Oils & Fats', 'Beverages',
    'Packaging', 'Cleaning Supplies', 'Other'
  ];

  const units = ['kg', 'gm', 'ltr', 'ml', 'pcs', 'box', 'packet', 'dozen'];

  // HSN Code Database
  const hsnCodes = {
    'Vegetables': '0701', 'Fruits': '0801', 'Meat & Poultry': '0207',
    'Seafood': '0302', 'Dairy': '0401', 'Grains & Cereals': '1006',
    'Spices': '0904', 'Oils & Fats': '1507', 'Beverages': '2202',
    'Packaging': '4819', 'Cleaning Supplies': '3402', 'Other': '9999'
  };

  const [itemForm, setItemForm] = useState({
    name: '', category: '', unit: 'kg', currentStock: 0,
    minStock: 0, maxStock: 0, unitPrice: 0, lastPurchaseDate: '',
    supplier: '', hsnCode: '', gstRate: 5
  });

  const [purchaseForm, setPurchaseForm] = useState({
    vendorId: '', 
    items: [{ 
      itemId: '', quantity: 0, unitPrice: 0, hsnCode: '',
      gstRate: 5, taxableAmount: 0, cgstAmount: 0, sgstAmount: 0,
      igstAmount: 0, totalAmount: 0
    }],
    purchaseDate: new Date().toISOString().split('T')[0],
    invoiceNumber: '', invoiceDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Cash', paymentDate: '', tdsDeducted: 0, tdsPercentage: 0,
    notes: '', isInterState: false,
    totalTaxableAmount: 0, totalCGST: 0, totalSGST: 0, totalIGST: 0,
    totalTax: 0, grandTotal: 0
  });

  const [vendorForm, setVendorForm] = useState({
    name: '', contactPerson: '', phone: '', email: '',
    address: '', gstin: '', stateCode: '', category: '', paymentTerms: ''
  });

  useEffect(() => {
     loadRestaurantData();
    loadInventoryData();
    loadRestaurantGSTIN();

  }, [restaurantId]);

  const loadRestaurantGSTIN = async () => {
    try {
      const restaurant = await StorageService.getRestaurant(restaurantId);
      if (restaurant?.gstin) {
        setRestaurantGSTIN(restaurant.gstin);
      }
    } catch (error) {
      console.error('Error loading restaurant GSTIN:', error);
    }
  };

  const loadInventoryData = async () => {
    try {
      setLoading(true);
      
      const itemsSnapshot = await getDocs(collection(db, 'restaurants', restaurantId, 'inventory_items'));
      const itemsData = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setItems(itemsData);

      const purchasesSnapshot = await getDocs(collection(db, 'restaurants', restaurantId, 'inventory_purchases'));
      const purchasesData = purchasesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPurchases(purchasesData.sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate)));

      const vendorsSnapshot = await getDocs(collection(db, 'restaurants', restaurantId, 'inventory_vendors'));
      const vendorsData = vendorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVendors(vendorsData);

      console.log('✅ Inventory data loaded');
    } catch (error) {
      console.error('❌ Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurantData = async () => {
  try {
    const rest = await StorageService.getRestaurant(restaurantId);
    setRestaurant(rest);
  } catch (error) {
    console.error('Error loading restaurant:', error);
  }
};

  // GST Calculation Helper
  const calculateGST = (taxableAmount, gstRate, isInterState) => {
    const gstAmount = (taxableAmount * gstRate) / 100;
    
    if (isInterState) {
      return { cgst: 0, sgst: 0, igst: gstAmount };
    } else {
      return { cgst: gstAmount / 2, sgst: gstAmount / 2, igst: 0 };
    }
  };

  // Check if inter-state transaction
  const checkInterState = (vendorGSTIN) => {
    if (!vendorGSTIN || !restaurantGSTIN) return false;
    const vendorState = vendorGSTIN.substring(0, 2);
    const restaurantState = restaurantGSTIN.substring(0, 2);
    return vendorState !== restaurantState;
  };

  // Safe number accessor
  const safeNumber = (value) => {
    return typeof value === 'number' && !isNaN(value) ? value : 0;
  };

  const dashboardStats = {
    totalItems: items.length,
    lowStockItems: items.filter(item => safeNumber(item.currentStock) <= safeNumber(item.minStock)).length,
    totalValue: items.reduce((sum, item) => sum + (safeNumber(item.currentStock) * safeNumber(item.unitPrice)), 0),
    monthlyPurchases: purchases.filter(p => {
      const purchaseDate = new Date(p.purchaseDate);
      const now = new Date();
      return purchaseDate.getMonth() === now.getMonth() && 
             purchaseDate.getFullYear() === now.getFullYear();
    }).length,
    monthlyExpense: purchases
      .filter(p => {
        const purchaseDate = new Date(p.purchaseDate);
        const now = new Date();
        return purchaseDate.getMonth() === now.getMonth() && 
               purchaseDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, p) => sum + safeNumber(p.grandTotal), 0),
    totalGSTPaid: purchases.reduce((sum, p) => sum + safeNumber(p.totalTax), 0),
    lastMonthExpense: purchases
      .filter(p => {
        const purchaseDate = new Date(p.purchaseDate);
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return purchaseDate.getMonth() === lastMonth.getMonth() && 
               purchaseDate.getFullYear() === lastMonth.getFullYear();
      })
      .reduce((sum, p) => sum + safeNumber(p.grandTotal), 0),
    avgPurchaseValue: purchases.length > 0 
      ? purchases.reduce((sum, p) => sum + safeNumber(p.grandTotal), 0) / purchases.length 
      : 0,
    topVendors: vendors.slice(0, 5).map(vendor => {
      const vendorPurchases = purchases.filter(p => p.vendorId === vendor.id);
      return {
        ...vendor,
        totalSpent: vendorPurchases.reduce((sum, p) => sum + safeNumber(p.grandTotal), 0),
        purchaseCount: vendorPurchases.length
      };
    }).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 3),
    categoryWiseSpending: categories.map(category => {
      const categoryItems = items.filter(item => item.category === category);
      const spending = categoryItems.reduce((sum, item) => {
        const itemPurchases = purchases.flatMap(p => p.items || [])
          .filter(pItem => pItem.itemId === item.id);
        return sum + itemPurchases.reduce((pSum, pItem) => pSum + safeNumber(pItem.totalAmount), 0);
      }, 0);
      return { category, spending, items: categoryItems.length };
    }).filter(c => c.spending > 0).sort((a, b) => b.spending - a.spending).slice(0, 5)
  };

  const expenseTrend = dashboardStats.monthlyExpense - dashboardStats.lastMonthExpense;
  const expenseChange = dashboardStats.lastMonthExpense > 0 
    ? ((expenseTrend / dashboardStats.lastMonthExpense) * 100).toFixed(1)
    : 0;

  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      const itemData = {
        ...itemForm,
        hsnCode: itemForm.hsnCode || hsnCodes[itemForm.category] || '9999'
      };

      if (editingItem) {
        const itemRef = doc(db, 'restaurants', restaurantId, 'inventory_items', editingItem.id);
        await updateDoc(itemRef, { ...itemData, updatedAt: new Date().toISOString() });
      } else {
        const newId = `item_${Date.now()}`;
        const itemRef = doc(db, 'restaurants', restaurantId, 'inventory_items', newId);
        await setDoc(itemRef, { id: newId, ...itemData, createdAt: new Date().toISOString() });
      }

      closeModal();
      await loadInventoryData();
      StorageService.clearCache();
    } catch (error) {
      console.error('❌ Error saving item:', error);
      alert('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      setSaving(true);
      await deleteDoc(doc(db, 'restaurants', restaurantId, 'inventory_items', itemId));
      await loadInventoryData();
      StorageService.clearCache();
    } catch (error) {
      console.error('❌ Error deleting item:', error);
      alert('Failed to delete item');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePurchase = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      const newId = `purchase_${Date.now()}`;
      const purchaseRef = doc(db, 'restaurants', restaurantId, 'inventory_purchases', newId);
      
      await setDoc(purchaseRef, {
        id: newId,
        ...purchaseForm,
        status: 'Completed',
        createdAt: new Date().toISOString()
      });

      // Update stock quantities
      for (const item of purchaseForm.items) {
        const inventoryItem = items.find(i => i.id === item.itemId);
        if (inventoryItem) {
          const itemRef = doc(db, 'restaurants', restaurantId, 'inventory_items', item.itemId);
          await updateDoc(itemRef, {
            currentStock: safeNumber(inventoryItem.currentStock) + safeNumber(item.quantity),
            unitPrice: item.unitPrice,
            lastPurchaseDate: purchaseForm.purchaseDate,
            updatedAt: new Date().toISOString()
          });
        }
      }

      closeModal();
      await loadInventoryData();
      StorageService.clearCache();
      alert('✅ Purchase recorded successfully!');
    } catch (error) {
      console.error('❌ Error saving purchase:', error);
      alert('Failed to save purchase');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      const vendorData = {
        ...vendorForm,
        stateCode: vendorForm.gstin ? vendorForm.gstin.substring(0, 2) : ''
      };

      if (editingItem) {
        await updateDoc(
          doc(db, 'restaurants', restaurantId, 'inventory_vendors', editingItem.id),
          { ...vendorData, updatedAt: new Date().toISOString() }
        );
      } else {
        const newId = `vendor_${Date.now()}`;
        await setDoc(
          doc(db, 'restaurants', restaurantId, 'inventory_vendors', newId),
          { id: newId, ...vendorData, createdAt: new Date().toISOString() }
        );
      }

      closeModal();
      await loadInventoryData();
      StorageService.clearCache();
    } catch (error) {
      console.error('❌ Error saving vendor:', error);
      alert('Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const updatePurchaseItem = (index, field, value) => {
    const newItems = [...purchaseForm.items];
    newItems[index][field] = value;

    // Auto-calculate if quantity or price changes
    if (field === 'quantity' || field === 'unitPrice' || field === 'gstRate') {
      const item = newItems[index];
      item.taxableAmount = safeNumber(item.quantity) * safeNumber(item.unitPrice);
      
      const gst = calculateGST(item.taxableAmount, safeNumber(item.gstRate), purchaseForm.isInterState);
      item.cgstAmount = gst.cgst;
      item.sgstAmount = gst.sgst;
      item.igstAmount = gst.igst;
      item.totalAmount = item.taxableAmount + gst.cgst + gst.sgst + gst.igst;
    }

    // Recalculate totals
    const totals = newItems.reduce((acc, item) => ({
      taxable: acc.taxable + safeNumber(item.taxableAmount),
      cgst: acc.cgst + safeNumber(item.cgstAmount),
      sgst: acc.sgst + safeNumber(item.sgstAmount),
      igst: acc.igst + safeNumber(item.igstAmount),
      total: acc.total + safeNumber(item.totalAmount)
    }), { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 });

    setPurchaseForm({
      ...purchaseForm,
      items: newItems,
      totalTaxableAmount: totals.taxable,
      totalCGST: totals.cgst,
      totalSGST: totals.sgst,
      totalIGST: totals.igst,
      totalTax: totals.cgst + totals.sgst + totals.igst,
      grandTotal: totals.total - safeNumber(purchaseForm.tdsDeducted)
    });
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    
    if (type === 'item') {
      setItemForm(item || {
        name: '', category: '', unit: 'kg', currentStock: 0,
        minStock: 0, maxStock: 0, unitPrice: 0, lastPurchaseDate: '',
        supplier: '', hsnCode: '', gstRate: 5
      });
    } else if (type === 'vendor') {
      setVendorForm(item || {
        name: '', contactPerson: '', phone: '', email: '',
        address: '', gstin: '', stateCode: '', category: '', paymentTerms: ''
      });
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditingItem(null);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // ITR-COMPLIANT EXPORT
  const exportITRCompliantReport = () => {
    const headers = [
      'Purchase Date', 'Invoice Date', 'Invoice Number', 'Vendor Name', 'Vendor GSTIN',
      'Item Name', 'HSN Code', 'Quantity', 'Unit', 'Rate', 'Taxable Amount',
      'CGST %', 'CGST Amount', 'SGST %', 'SGST Amount', 'IGST %', 'IGST Amount',
      'Total Tax', 'Total Amount', 'Payment Mode', 'Payment Date', 'TDS Deducted', 'TDS %'
    ];

    const rows = purchases.flatMap(purchase => {
      const vendor = vendors.find(v => v.id === purchase.vendorId);
      return (purchase.items || []).map(item => {
        const inventoryItem = items.find(i => i.id === item.itemId);
        const cgstRate = safeNumber(item.cgstAmount) > 0 ? safeNumber(item.gstRate) / 2 : 0;
        const sgstRate = safeNumber(item.sgstAmount) > 0 ? safeNumber(item.gstRate) / 2 : 0;
        const igstRate = safeNumber(item.igstAmount) > 0 ? safeNumber(item.gstRate) : 0;

        return [
          purchase.purchaseDate,
          purchase.invoiceDate || purchase.purchaseDate,
          purchase.invoiceNumber || 'N/A',
          vendor?.name || 'Unknown',
          vendor?.gstin || 'N/A',
          inventoryItem?.name || 'Unknown',
          item.hsnCode || inventoryItem?.hsnCode || 'N/A',
          safeNumber(item.quantity),
          inventoryItem?.unit || 'pcs',
          safeNumber(item.unitPrice).toFixed(2),
          safeNumber(item.taxableAmount).toFixed(2),
          cgstRate.toFixed(2),
          safeNumber(item.cgstAmount).toFixed(2),
          sgstRate.toFixed(2),
          safeNumber(item.sgstAmount).toFixed(2),
          igstRate.toFixed(2),
          safeNumber(item.igstAmount).toFixed(2),
          (safeNumber(item.cgstAmount) + safeNumber(item.sgstAmount) + safeNumber(item.igstAmount)).toFixed(2),
          safeNumber(item.totalAmount).toFixed(2),
          purchase.paymentMethod,
          purchase.paymentDate || purchase.purchaseDate,
          safeNumber(purchase.tdsDeducted),
          safeNumber(purchase.tdsPercentage)
        ].join(',');
      });
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Purchase_Register_ITR_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // GSTR-2 FORMAT EXPORT
  const exportGSTR2Format = () => {
    const headers = [
      'GSTIN of Supplier', 'Invoice Number', 'Invoice Date',
      'Invoice Value', 'Taxable Value', 'IGST', 'CGST', 'SGST',
      'ITC Available', 'Reason for ITC Not Available'
    ];

    const rows = purchases.map(purchase => {
      const vendor = vendors.find(v => v.id === purchase.vendorId);
      return [
        vendor?.gstin || 'UNREGISTERED',
        purchase.invoiceNumber || 'N/A',
        purchase.invoiceDate || purchase.purchaseDate,
        safeNumber(purchase.grandTotal).toFixed(2),
        safeNumber(purchase.totalTaxableAmount).toFixed(2),
        safeNumber(purchase.totalIGST).toFixed(2),
        safeNumber(purchase.totalCGST).toFixed(2),
        safeNumber(purchase.totalSGST).toFixed(2),
        vendor?.gstin ? 'Yes' : 'No',
        vendor?.gstin ? '' : 'Unregistered Supplier'
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `GSTR2_Format_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

 if (!loading && restaurant) {
    const plan = restaurant.package?.toLowerCase();
    const hasInventoryAccess = plan === 'premium' || plan === 'advanced' || plan ==='basic';
    if (!hasInventoryAccess) {
        return (
            <div className="p-6 min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
                <div className="max-w-2xl mx-auto mt-20">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center border-4 border-orange-200">
                        <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Lock className="w-10 h-10 text-red-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">Inventory Access Restricted</h2>
                        <p className="text-lg text-gray-600 mb-2">
                            You are currently on the <span className="font-bold text-orange-600">{restaurant.package || 'Basic'} Plan</span>
                        </p>
                        <p className="text-gray-600 mb-8">
                            Inventory Management is only available for <span className="font-bold">Premium</span> and <span className="font-bold">Advanced</span> plan users.
                        </p>
                        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-6 mb-8">
                            <h3 className="font-bold text-gray-800 mb-4">Upgrade to access:</h3>
                            <ul className="text-left space-y-2 text-gray-700">
                                <li className="flex items-center gap-2">
                                    <Check className="w-5 h-5 text-green-600" />
                                    GST &amp; ITR Compliant Inventory System
                                </li>
                                <li className="flex items-center gap-2">
                                    <Check className="w-5 h-5 text-green-600" />
                                    Track Stock Levels &amp; Low Stock Alerts
                                </li>
                                <li className="flex items-center gap-2">
                                    <Check className="w-5 h-5 text-green-600" />
                                    Purchase Management with Invoices
                                </li>
                                <li className="flex items-center gap-2">
                                    <Check className="w-5 h-5 text-green-600" />
                                    Vendor Management &amp; GSTR-2 Reports
                                </li>
                                <li className="flex items-center gap-2">
                                    <Check className="w-5 h-5 text-green-600" />
                                    Category-wise Spending Analytics
                                </li>
                            </ul>
                        </div>
                        <a 
                            className="inline-flex items-center gap-2 bg-orange-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-600 transition shadow-lg"
                            href={'mailto:contact@zyppro.com?subject=Upgrade%20to%20Premium%20Plan&body=Hi%20Zyppro%20Team,%0D%0A%0D%0AI%20would%20like%20to%20upgrade%20to%20Premium%20or%20Advanced%20plan%20to%20access%20Inventory%20Management.%0D%0A%0D%0ARestaurant:%20' + encodeURIComponent(restaurant.name) + '%0D%0ACurrent%20Plan:%20' + encodeURIComponent(restaurant.package || 'Basic') + '%0D%0A%0D%0APlease%20contact%20me.%0D%0A%0D%0AThank%20you'}
                        >
                            {/* NOTE: You need to ensure 'Crown' is imported from 'lucide-react' */}
                            <Crown className="w-6 h-6" />
                            Contact Us to Upgrade
                        </a>
                        <p className="text-sm text-gray-600 mt-4">
                            Email: <a href="mailto:contact@zyppro.com" className="text-orange-600 underline font-semibold">contact@zyppro.com</a>
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}
  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-8 h-8 text-orange-500" />
            Inventory Management
          </h1>
          <p className="text-sm text-gray-600 mt-1">GST & ITR Compliant System</p>
        </div>
      </div>

      {/* TAB NAVIGATION */}
      <div className="bg-white rounded-xl shadow-md p-2 mb-6 flex flex-wrap gap-2">
        {['dashboard', 'items', 'purchases', 'vendors', 'reports'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 min-w-[120px] px-4 py-3 rounded-lg font-semibold transition ${
              activeTab === tab ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab === 'dashboard' && <BarChart3 className="w-5 h-5 inline mr-2" />}
            {tab === 'items' && <Package className="w-5 h-5 inline mr-2" />}
            {tab === 'purchases' && <ShoppingCart className="w-5 h-5 inline mr-2" />}
            {tab === 'vendors' && <Users className="w-5 h-5 inline mr-2" />}
            {tab === 'reports' && <FileText className="w-5 h-5 inline mr-2" />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ENHANCED DASHBOARD TAB */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Main Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-blue-100 text-sm font-medium">Total Items</p>
                <Package className="w-8 h-8 text-blue-200" />
              </div>
              <p className="text-4xl font-bold mb-1">{dashboardStats.totalItems}</p>
              <p className="text-xs text-blue-100">{categories.length} categories</p>
            </div>

            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-red-100 text-sm font-medium">Low Stock Alert</p>
                <AlertTriangle className="w-8 h-8 text-red-200" />
              </div>
              <p className="text-4xl font-bold mb-1">{dashboardStats.lowStockItems}</p>
              <p className="text-xs text-red-100">items need restock</p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-green-100 text-sm font-medium">Stock Value</p>
                <DollarSign className="w-8 h-8 text-green-200" />
              </div>
              <p className="text-4xl font-bold mb-1">₹{(dashboardStats.totalValue / 1000).toFixed(1)}K</p>
              <p className="text-xs text-green-100">current inventory</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-purple-100 text-sm font-medium">GST Paid (ITC)</p>
                <IndianRupee className="w-8 h-8 text-purple-200" />
              </div>
              <p className="text-4xl font-bold mb-1">₹{(dashboardStats.totalGSTPaid / 1000).toFixed(1)}K</p>
              <p className="text-xs text-purple-100">input tax credit</p>
            </div>
          </div>

          {/* Monthly Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <ShoppingCart className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">This Month</p>
                  <p className="text-2xl font-bold text-gray-800">{dashboardStats.monthlyPurchases}</p>
                  <p className="text-xs text-gray-500">purchases</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Monthly Expense</p>
                  <p className="text-2xl font-bold text-gray-800">₹{(dashboardStats.monthlyExpense / 1000).toFixed(1)}K</p>
                  <div className="flex items-center gap-1 mt-1">
                    {expenseChange >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-red-500" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-green-500" />
                    )}
                    <p className={`text-xs font-medium ${expenseChange >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {Math.abs(expenseChange)}% vs last month
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Avg Purchase</p>
                  <p className="text-2xl font-bold text-gray-800">₹{dashboardStats.avgPurchaseValue.toFixed(0)}</p>
                  <p className="text-xs text-gray-500">per transaction</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Vendors */}
          {dashboardStats.topVendors.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                Top Vendors by Spending
              </h3>
              <div className="space-y-3">
                {dashboardStats.topVendors.map((vendor, idx) => (
                  <div key={vendor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-orange-400'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{vendor.name}</p>
                        <p className="text-xs text-gray-600">{vendor.purchaseCount} purchases</p>
                      </div>
                    </div>
                    <p className="text-lg font-bold text-green-600">₹{(vendor.totalSpent / 1000).toFixed(1)}K</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category-wise Spending */}
          {dashboardStats.categoryWiseSpending.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-500" />
                Category-wise Spending
              </h3>
              <div className="space-y-3">
                {dashboardStats.categoryWiseSpending.map((cat) => {
                  const maxSpending = Math.max(...dashboardStats.categoryWiseSpending.map(c => c.spending));
                  const percentage = (cat.spending / maxSpending) * 100;
                  return (
                    <div key={cat.category}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{cat.category}</span>
                        <span className="text-sm font-bold text-gray-800">₹{(cat.spending / 1000).toFixed(1)}K</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-gradient-to-r from-orange-500 to-red-500 h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{cat.items} items</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Low Stock Alert Section */}
          {dashboardStats.lowStockItems > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <h3 className="text-xl font-bold text-red-800">Low Stock Alert!</h3>
              </div>
              <div className="space-y-2">
                {items.filter(item => safeNumber(item.currentStock) <= safeNumber(item.minStock)).map(item => (
                  <div key={item.id} className="bg-white rounded-lg p-3 flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-800">{item.name}</p>
                      <p className="text-sm text-gray-600">{item.category} • HSN: {item.hsnCode}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-red-600 font-bold">{safeNumber(item.currentStock)} {item.unit}</p>
                      <p className="text-xs text-gray-500">Min: {safeNumber(item.minStock)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ITEMS TAB */}
      {activeTab === 'items' && (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option>All</option>
              {categories.map(cat => <option key={cat}>{cat}</option>)}
            </select>
            <button
              onClick={() => openModal('item')}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2 whitespace-nowrap"
            >
              <Plus className="w-5 h-5" />
              Add Item
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map(item => (
              <div key={item.id} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800">{item.name}</h3>
                    <p className="text-sm text-gray-600">{item.category}</p>
                    <p className="text-xs text-blue-600">HSN: {item.hsnCode} • GST: {item.gstRate}%</p>
                  </div>
                  {safeNumber(item.currentStock) <= safeNumber(item.minStock) && (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Stock:</span>
                    <span className={`font-bold ${safeNumber(item.currentStock) <= safeNumber(item.minStock) ? 'text-red-600' : 'text-green-600'}`}>
                      {safeNumber(item.currentStock)} {item.unit}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Price:</span>
                    <span className="text-gray-800">₹{safeNumber(item.unitPrice)}/{item.unit}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Value:</span>
                    <span className="font-bold text-orange-600">₹{(safeNumber(item.currentStock) * safeNumber(item.unitPrice)).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openModal('item', item)}
                    className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-1"
                  >
                    <Edit className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PURCHASES TAB */}
      {activeTab === 'purchases' && (
        <div className="space-y-6">
          <button
            onClick={() => {
              setPurchaseForm({
                vendorId: '', 
                items: [{ 
                  itemId: '', quantity: 0, unitPrice: 0, hsnCode: '',
                  gstRate: 5, taxableAmount: 0, cgstAmount: 0, sgstAmount: 0,
                  igstAmount: 0, totalAmount: 0
                }],
                purchaseDate: new Date().toISOString().split('T')[0],
                invoiceNumber: '', invoiceDate: new Date().toISOString().split('T')[0],
                paymentMethod: 'Cash', paymentDate: '', tdsDeducted: 0, tdsPercentage: 0,
                notes: '', isInterState: false,
                totalTaxableAmount: 0, totalCGST: 0, totalSGST: 0, totalIGST: 0,
                totalTax: 0, grandTotal: 0
              });
              setModalType('purchase');
              setShowModal(true);
            }}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Record Purchase
          </button>

          <div className="space-y-4">
            {purchases.map(purchase => {
              const vendor = vendors.find(v => v.id === purchase.vendorId);
              return (
                <div key={purchase.id} className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg">{vendor?.name || 'Unknown'}</h3>
                      <p className="text-sm text-gray-600">Invoice: {purchase.invoiceNumber || 'N/A'}</p>
                      <p className="text-sm text-gray-600">Date: {new Date(purchase.invoiceDate || purchase.purchaseDate).toLocaleDateString()}</p>
                      {vendor?.gstin && <p className="text-xs text-blue-600">GSTIN: {vendor.gstin}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-600">₹{safeNumber(purchase.grandTotal).toFixed(2)}</p>
                      <p className="text-sm text-gray-600">{purchase.paymentMethod}</p>
                      <p className="text-xs text-green-600">GST: ₹{safeNumber(purchase.totalTax).toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="border-t pt-4 mb-3">
                    <h4 className="font-semibold text-gray-700 mb-2">Items:</h4>
                    <div className="space-y-1">
                      {(purchase.items || []).map((item, idx) => {
                        const inventoryItem = items.find(i => i.id === item.itemId);
                        return (
                          <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                            <div className="flex justify-between">
                              <span className="font-medium">{inventoryItem?.name || 'Unknown'}</span>
                              <span className="font-bold">₹{safeNumber(item.totalAmount).toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              {safeNumber(item.quantity)} {inventoryItem?.unit} × ₹{safeNumber(item.unitPrice)} = ₹{safeNumber(item.taxableAmount).toFixed(2)}
                              {purchase.isInterState ? 
                                ` | IGST ${safeNumber(item.gstRate)}%: ₹${safeNumber(item.igstAmount).toFixed(2)}` :
                                ` | CGST ${(safeNumber(item.gstRate)/2).toFixed(1)}%: ₹${safeNumber(item.cgstAmount).toFixed(2)} | SGST ${(safeNumber(item.gstRate)/2).toFixed(1)}%: ₹${safeNumber(item.sgstAmount).toFixed(2)}`
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t pt-3 bg-blue-50 p-3 rounded-lg">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Taxable Amount:</span>
                        <span className="font-semibold">₹{safeNumber(purchase.totalTaxableAmount).toFixed(2)}</span>
                      </div>
                      {purchase.isInterState ? (
                        <div className="flex justify-between">
                          <span className="text-gray-600">IGST:</span>
                          <span className="font-semibold text-blue-600">₹{safeNumber(purchase.totalIGST).toFixed(2)}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">CGST:</span>
                            <span className="font-semibold text-green-600">₹{safeNumber(purchase.totalCGST).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">SGST:</span>
                            <span className="font-semibold text-green-600">₹{safeNumber(purchase.totalSGST).toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      {safeNumber(purchase.tdsDeducted) > 0 && (
                        <div className="flex justify-between col-span-2">
                          <span className="text-gray-600">TDS Deducted ({safeNumber(purchase.tdsPercentage)}%):</span>
                          <span className="font-semibold text-red-600">-₹{safeNumber(purchase.tdsDeducted).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VENDORS TAB */}
      {activeTab === 'vendors' && (
        <div className="space-y-6">
          <button
            onClick={() => openModal('vendor')}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Vendor
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(vendor => (
              <div key={vendor.id} className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-bold text-gray-800 text-lg mb-2">{vendor.name}</h3>
                <div className="space-y-1 text-sm mb-4">
                  <p className="text-gray-600"><strong>Contact:</strong> {vendor.contactPerson}</p>
                  <p className="text-gray-600"><strong>Phone:</strong> {vendor.phone}</p>
                  <p className="text-gray-600"><strong>Email:</strong> {vendor.email}</p>
                  {vendor.gstin && (
                    <p className="text-blue-600 font-medium"><strong>GSTIN:</strong> {vendor.gstin}</p>
                  )}
                  <p className="text-gray-600"><strong>Category:</strong> {vendor.category}</p>
                  {vendor.paymentTerms && (
                    <p className="text-gray-600"><strong>Terms:</strong> {vendor.paymentTerms}</p>
                  )}
                </div>
                <button
                  onClick={() => openModal('vendor', vendor)}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Vendor
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REPORTS TAB */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">GST & ITR Compliant Reports</h3>
            <p className="text-sm text-gray-600 mb-4">Export CA-ready reports for tax filing</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={exportITRCompliantReport}
                className="bg-green-500 text-white px-6 py-4 rounded-lg hover:bg-green-600 flex flex-col items-center gap-2 transition"
              >
                <Download className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-bold">ITR Purchase Register</p>
                  <p className="text-xs">Complete with GST breakdown</p>
                </div>
              </button>

              <button
                onClick={exportGSTR2Format}
                className="bg-blue-500 text-white px-6 py-4 rounded-lg hover:bg-blue-600 flex flex-col items-center gap-2 transition"
              >
                <Download className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-bold">GSTR-2 Format</p>
                  <p className="text-xs">Ready for GST filing</p>
                </div>
              </button>

              <button
                onClick={() => {
                  const data = vendors.map(v => ({
                    Name: v.name,
                    GSTIN: v.gstin || 'Unregistered',
                    Contact: v.contactPerson,
                    Phone: v.phone,
                    Email: v.email,
                    Category: v.category,
                    Terms: v.paymentTerms
                  }));
                  const csv = [Object.keys(data[0]).join(','), ...data.map(row => Object.values(row).join(','))].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `Vendors_${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                }}
                className="bg-purple-500 text-white px-6 py-4 rounded-lg hover:bg-purple-600 flex flex-col items-center gap-2 transition"
              >
                <Download className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-bold">Vendor Master</p>
                  <p className="text-xs">With GSTIN details</p>
                </div>
              </button>
            </div>
          </div>

          {/* Monthly GST Summary */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Monthly GST Summary</h3>
            <div className="space-y-3">
              {Array.from({ length: 6 }, (_, i) => {
                const date = new Date();
                date.setMonth(date.getMonth() - i);
                const month = date.toLocaleString('default', { month: 'long', year: 'numeric' });
                
                const monthPurchases = purchases.filter(p => {
                  const pDate = new Date(p.purchaseDate);
                  return pDate.getMonth() === date.getMonth() && pDate.getFullYear() === date.getFullYear();
                });
                
                const totals = monthPurchases.reduce((acc, p) => ({
                  taxable: acc.taxable + safeNumber(p.totalTaxableAmount),
                  cgst: acc.cgst + safeNumber(p.totalCGST),
                  sgst: acc.sgst + safeNumber(p.totalSGST),
                  igst: acc.igst + safeNumber(p.totalIGST),
                  total: acc.total + safeNumber(p.grandTotal)
                }), { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 });
                
                return (
                  <div key={i} className="border-b pb-3">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <p className="font-semibold text-gray-800">{month}</p>
                        <p className="text-sm text-gray-600">{monthPurchases.length} purchases</p>
                      </div>
                      <p className="text-xl font-bold text-orange-600">₹{totals.total.toFixed(2)}</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-600">Taxable</p>
                        <p className="font-bold">₹{totals.taxable.toFixed(0)}</p>
                      </div>
                      <div className="bg-green-50 p-2 rounded">
                        <p className="text-gray-600">CGST</p>
                        <p className="font-bold text-green-600">₹{totals.cgst.toFixed(0)}</p>
                      </div>
                      <div className="bg-green-50 p-2 rounded">
                        <p className="text-gray-600">SGST</p>
                        <p className="font-bold text-green-600">₹{totals.sgst.toFixed(0)}</p>
                      </div>
                      <div className="bg-blue-50 p-2 rounded">
                        <p className="text-gray-600">IGST</p>
                        <p className="font-bold text-blue-600">₹{totals.igst.toFixed(0)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* HSN-wise Summary */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">HSN-wise Stock Value</h3>
            <div className="space-y-2">
              {Object.entries(
                items.reduce((acc, item) => {
                  const hsn = item.hsnCode || '9999';
                  if (!acc[hsn]) acc[hsn] = { name: item.category, value: 0, items: 0 };
                  acc[hsn].value += safeNumber(item.currentStock) * safeNumber(item.unitPrice);
                  acc[hsn].items++;
                  return acc;
                }, {})
              ).map(([hsn, data]) => (
                <div key={hsn} className="flex justify-between items-center border-b pb-2">
                  <div>
                    <p className="font-semibold text-gray-800">HSN: {hsn}</p>
                    <p className="text-sm text-gray-600">{data.name} • {data.items} items</p>
                  </div>
                  <p className="text-lg font-bold text-green-600">₹{data.value.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ITEM MODAL */}
      {showModal && modalType === 'item' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Item Name *</label>
                  <input
                    type="text"
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={itemForm.category}
                    onChange={(e) => setItemForm({ 
                      ...itemForm, 
                      category: e.target.value,
                      hsnCode: hsnCodes[e.target.value] || '9999'
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Unit *</label>
                  <select
                    value={itemForm.unit}
                    onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    {units.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">HSN Code *</label>
                  <input
                    type="text"
                    value={itemForm.hsnCode}
                    onChange={(e) => setItemForm({ ...itemForm, hsnCode: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="e.g., 0701"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">GST Rate (%) *</label>
                  <select
                    value={itemForm.gstRate}
                    onChange={(e) => setItemForm({ ...itemForm, gstRate: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="0">0% (Exempt)</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Stock *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.currentStock}
                    onChange={(e) => setItemForm({ ...itemForm, currentStock: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Min Stock *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.minStock}
                    onChange={(e) => setItemForm({ ...itemForm, minStock: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Stock</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.maxStock}
                    onChange={(e) => setItemForm({ ...itemForm, maxStock: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Unit Price (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.unitPrice}
                    onChange={(e) => setItemForm({ ...itemForm, unitPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Supplier/Vendor</label>
                  <input
                    type="text"
                    value={itemForm.supplier}
                    onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="Quick reference"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <><Loader className="w-5 h-5 animate-spin" /> Saving...</> : <>{editingItem ? 'Update' : 'Add'} Item</>}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* PURCHASE MODAL */}
      {showModal && modalType === 'purchase' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-8 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white pb-4 border-b">
              <h2 className="text-2xl font-bold text-gray-800">Record New Purchase</h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSavePurchase} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vendor *</label>
                  <select
                    value={purchaseForm.vendorId}
                    onChange={(e) => {
                      const vendor = vendors.find(v => v.id === e.target.value);
                      const isInterState = vendor ? checkInterState(vendor.gstin) : false;
                      setPurchaseForm({ ...purchaseForm, vendorId: e.target.value, isInterState });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name} {vendor.gstin ? `(${vendor.gstin})` : ''}
                      </option>
                    ))}
                  </select>
                  {purchaseForm.isInterState && (
                    <p className="text-xs text-blue-600 mt-1">Inter-state purchase (IGST applicable)</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date *</label>
                  <input
                    type="date"
                    value={purchaseForm.purchaseDate}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, purchaseDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                  <input
                    type="text"
                    value={purchaseForm.invoiceNumber}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, invoiceNumber: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="Vendor's invoice no"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date</label>
                  <input
                    type="date"
                    value={purchaseForm.invoiceDate}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, invoiceDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                  <select
                    value={purchaseForm.paymentMethod}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, paymentMethod: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option>Cash</option>
                    <option>Card</option>
                    <option>UPI</option>
                    <option>Bank Transfer</option>
                    <option>Credit</option>
                    <option>Cheque</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                  <input
                    type="date"
                    value={purchaseForm.paymentDate}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, paymentDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TDS %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={purchaseForm.tdsPercentage}
                    onChange={(e) => {
                      const tdsPercent = parseFloat(e.target.value) || 0;
                      const tdsAmount = (purchaseForm.totalTaxableAmount * tdsPercent) / 100;
                      setPurchaseForm({ 
                        ...purchaseForm, 
                        tdsPercentage: tdsPercent,
                        tdsDeducted: tdsAmount,
                        grandTotal: purchaseForm.totalTaxableAmount + purchaseForm.totalTax - tdsAmount
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TDS Deducted (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={purchaseForm.tdsDeducted}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-gray-800">Purchase Items</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setPurchaseForm({
                        ...purchaseForm,
                        items: [...purchaseForm.items, { 
                          itemId: '', quantity: 0, unitPrice: 0, hsnCode: '',
                          gstRate: 5, taxableAmount: 0, cgstAmount: 0, sgstAmount: 0,
                          igstAmount: 0, totalAmount: 0
                        }]
                      });
                    }}
                    className="text-orange-500 hover:text-orange-600 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>

                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {purchaseForm.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="grid grid-cols-12 gap-2 items-end mb-2">
                        <div className="col-span-4">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Item *</label>
                          <select
                            value={item.itemId}
                            onChange={(e) => {
                              const selectedItem = items.find(i => i.id === e.target.value);
                              if (selectedItem) {
                                updatePurchaseItem(index, 'itemId', e.target.value);
                                updatePurchaseItem(index, 'unitPrice', selectedItem.unitPrice);
                                updatePurchaseItem(index, 'hsnCode', selectedItem.hsnCode);
                                updatePurchaseItem(index, 'gstRate', selectedItem.gstRate);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            required
                          >
                            <option value="">Select Item</option>
                            {items.map(item => (
                              <option key={item.id} value={item.id}>
                                {item.name} ({item.unit})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Qty *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => updatePurchaseItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Rate (₹) *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) => updatePurchaseItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">HSN</label>
                          <input
                            type="text"
                            value={item.hsnCode}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100"
                          />
                        </div>

                        <div className="col-span-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">GST%</label>
                          <input
                            type="number"
                            value={item.gstRate}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100"
                          />
                        </div>

                        <div className="col-span-1">
                          {purchaseForm.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newItems = purchaseForm.items.filter((_, i) => i !== index);
                                setPurchaseForm({ ...purchaseForm, items: newItems });
                              }}
                              className="w-full p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-6 gap-2 text-xs bg-white p-2 rounded">
                        <div>
                          <span className="text-gray-600">Taxable:</span>
                          <span className="font-bold ml-1">₹{item.taxableAmount.toFixed(2)}</span>
                        </div>
                        {purchaseForm.isInterState ? (
                          <div className="col-span-2">
                            <span className="text-gray-600">IGST:</span>
                            <span className="font-bold ml-1 text-blue-600">₹{item.igstAmount.toFixed(2)}</span>
                          </div>
                        ) : (
                          <>
                            <div>
                              <span className="text-gray-600">CGST:</span>
                              <span className="font-bold ml-1 text-green-600">₹{item.cgstAmount.toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">SGST:</span>
                              <span className="font-bold ml-1 text-green-600">₹{item.sgstAmount.toFixed(2)}</span>
                            </div>
                          </>
                        )}
                        <div className="col-span-2 text-right">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-bold ml-1 text-orange-600">₹{item.totalAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border-2 border-orange-200">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">Taxable Amount:</span>
                      <span className="font-bold">₹{purchaseForm.totalTaxableAmount.toFixed(2)}</span>
                    </div>
                    {purchaseForm.isInterState ? (
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">IGST:</span>
                        <span className="font-bold text-blue-600">₹{purchaseForm.totalIGST.toFixed(2)}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="font-semibold text-gray-700">CGST:</span>
                          <span className="font-bold text-green-600">₹{purchaseForm.totalCGST.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-semibold text-gray-700">SGST:</span>
                          <span className="font-bold text-green-600">₹{purchaseForm.totalSGST.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between col-span-2 border-t pt-2">
                      <span className="font-semibold text-gray-700">Total Tax:</span>
                      <span className="font-bold text-purple-600">₹{purchaseForm.totalTax.toFixed(2)}</span>
                    </div>
                    {purchaseForm.tdsDeducted > 0 && (
                      <div className="flex justify-between col-span-2">
                        <span className="font-semibold text-gray-700">TDS Deducted:</span>
                        <span className="font-bold text-red-600">-₹{purchaseForm.tdsDeducted.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between col-span-2 border-t pt-2">
                      <span className="font-bold text-lg text-gray-800">Grand Total:</span>
                      <span className="font-bold text-2xl text-orange-600">₹{purchaseForm.grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={purchaseForm.notes}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Recording Purchase...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Record Purchase
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* VENDOR MODAL */}
      {showModal && modalType === 'vendor' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingItem ? 'Edit Vendor' : 'Add New Vendor'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSaveVendor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Name *</label>
                  <input
                    type="text"
                    value={vendorForm.name}
                    onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                  <input
                    type="text"
                    value={vendorForm.contactPerson}
                    onChange={(e) => setVendorForm({ ...vendorForm, contactPerson: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={vendorForm.phone}
                    onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={vendorForm.email}
                    onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">GSTIN</label>
                  <input
                    type="text"
                    value={vendorForm.gstin}
                    onChange={(e) => setVendorForm({ ...vendorForm, gstin: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="29ABCDE1234F1Z5"
                    maxLength="15"
                  />
                  <p className="text-xs text-gray-500 mt-1">Required for Input Tax Credit (ITC)</p>
                </div>





                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <textarea
                    value={vendorForm.address}
                    onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={vendorForm.category}
                    onChange={(e) => setVendorForm({ ...vendorForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                  <input
                    type="text"
                    value={vendorForm.paymentTerms}
                    onChange={(e) => setVendorForm({ ...vendorForm, paymentTerms: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="e.g., Net 30 days"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>{editingItem ? 'Update' : 'Add'} Vendor</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;